import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ReactFlowProvider, useEdgesState, useNodesState, Node, Edge } from 'reactflow';
import NodePalette from './components/NodePalette';
import PipelineCanvas from './components/PipelineCanvas';
import NodeConfigPanel from './components/NodeConfigPanel';
import PipelineExecutionPanel from './components/PipelineExecutionPanel';
import { ExecutionHistoryPanel } from './components/ExecutionHistoryPanel';
import { ExecutionLogsViewer } from './components/ExecutionLogsViewer';
import { ApiKeyManagement } from './components/ApiKeyManagement';
import { PublishDatasetDialog } from './components/PublishDatasetDialog';
import { ScheduleEditorDialog } from './components/ScheduleEditorDialog';
import { FloatingPreviewPanel } from './components/FloatingPreviewPanel';
import { PipelineHealthIndicator } from './components/PipelineHealthIndicator';
import { ValidationPanel } from './components/ValidationPanel';
import { PipelineBuilderChatPanel } from './components/ai-chat/PipelineBuilderChatPanel';
import { InsightPanel } from './components/ai-chat/InsightPanel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { PipelineEdge, PipelineNode, PipelineNodeType, PipelineNodeData, ScheduleConfig } from './types/pipeline';
import { useCreatePipeline, usePipeline, useUpdatePipeline } from './hooks/use-pipelines';
import { useUpdatePipelineSchedule, useDeletePipelineSchedule } from './hooks/use-pipeline-schedule';
import { usePreviewData } from './hooks/use-preview-data';
import { usePipelineChatStore } from '@/store/pipeline-chat-store';
import { toast } from 'sonner';
import { Share2, HelpCircle, ArrowLeft, RefreshCw, CalendarClock } from 'lucide-react';
import { usePipelineValidation } from '@/hooks/use-pipeline-validation';
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';

function isValidGeneratedNode(value: unknown): value is Node<PipelineNodeData> {
  if (!value || typeof value !== 'object') return false;
  const node = value as Record<string, unknown>;
  if (typeof node.id !== 'string' || node.id.trim().length === 0) return false;
  const position = node.position as Record<string, unknown> | undefined;
  if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') return false;
  const data = node.data as Record<string, unknown> | undefined;
  if (!data || typeof data.type !== 'string') return false;
  return true;
}

function isValidGeneratedEdge(value: unknown): value is Edge {
  if (!value || typeof value !== 'object') return false;
  const edge = value as Record<string, unknown>;
  return (
    typeof edge.source === 'string' &&
    edge.source.trim().length > 0 &&
    typeof edge.target === 'string' &&
    edge.target.trim().length > 0
  );
}

// Keyboard shortcuts help component
function KeyboardShortcutsHelp() {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(!open)}
        className="gap-2"
      >
        <HelpCircle className="h-4 w-4" />
        Shortcuts
      </Button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-lg border bg-white shadow-lg p-4 z-50">
          <h3 className="font-semibold mb-3 text-sm">Keyboard Shortcuts</h3>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-600">Save</span>
              <kbd className="px-2 py-1 bg-gray-100 rounded">Ctrl+S</kbd>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Auto-layout</span>
              <kbd className="px-2 py-1 bg-gray-100 rounded">Ctrl+A</kbd>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Delete node</span>
              <kbd className="px-2 py-1 bg-gray-100 rounded">Del</kbd>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Preview data</span>
              <kbd className="px-2 py-1 bg-gray-100 rounded">Shift+P</kbd>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PipelineBuilderPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const pipelineIdFromUrl = params?.id === 'new' ? null : params?.id || null;
  const pipelineChatKey = pipelineIdFromUrl || 'draft-builder';
  const activeProposal = usePipelineChatStore(
    (state) => state.byPipeline[pipelineChatKey]?.proposal ?? null,
  );

  const initialNodes: PipelineNode[] = useMemo(
    () => [
      {
        id: 'source-1',
        type: 'SOURCE',
        position: { x: 80, y: 100 },
        data: { label: 'Data Source', type: 'SOURCE', description: 'Extract from API' },
      },
      {
        id: 'transform-1',
        type: 'TRANSFORMATION',
        position: { x: 360, y: 120 },
        data: { label: 'Transform', type: 'TRANSFORMATION', description: 'Clean & normalize' },
      },
      {
        id: 'destination-1',
        type: 'DESTINATION',
        position: { x: 640, y: 140 },
        data: { label: 'Destination', type: 'DESTINATION', description: 'Load to warehouse' },
      },
    ],
    []
  );

  const initialEdges: PipelineEdge[] = useMemo(
    () => [
      { id: 'e1-2', source: 'source-1', target: 'transform-1' },
      { id: 'e2-3', source: 'transform-1', target: 'destination-1' },
    ],
    []
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes as Node<PipelineNodeData>[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges as Edge[]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [pipelineName, setPipelineName] = useState('New Pipeline');
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewNodeId, setPreviewNodeId] = useState<string | null>(null);

  const selectedNode = useMemo(
    () => (nodes.find((node) => node.id === selectedNodeId) || null) as PipelineNode | null,
    [nodes, selectedNodeId]
  );

  const { data: pipelineData, refetch: refetchPipeline } = usePipeline(pipelineIdFromUrl);
  const createPipeline = useCreatePipeline();
  const updatePipeline = useUpdatePipeline(pipelineIdFromUrl || '');
  const updateSchedule = useUpdatePipelineSchedule(pipelineIdFromUrl || '');
  const deleteSchedule = useDeletePipelineSchedule(pipelineIdFromUrl || '');

  // Validation
  const validationResult = usePipelineValidation({
    nodes: nodes as PipelineNode[],
    edges: edges as PipelineEdge[],
  });

  // Keyboard shortcuts
  useKeyboardShortcuts(
    {
      's': () => {
        handleSave();
      },
      'a': () => handleAutoLayout(),
      'f': () => {
        // Fit view - handled by React Flow
        const reactFlowInstance = document.querySelector('.react-flow');
        if (reactFlowInstance) {
          // Trigger fit view
          handleAutoLayout(); // Temporary - proper fitView needs instance
        }
      },
    },
    { ctrl: true }
  );

  useKeyboardShortcuts({
    'Delete': () => {
      if (selectedNodeId) {
        setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
        setEdges((eds) =>
          eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId)
        );
        setSelectedNodeId(null);
        toast.success('Node deleted');
      }
    },
  });

  // Preview state
  const previewNode = useMemo(
    () => (nodes.find((n) => n.id === previewNodeId) || null) as PipelineNode | null,
    [nodes, previewNodeId]
  );

  const {
    data: previewData,
    isLoading: previewLoading,
    error: previewError,
    refetch: refetchPreview,
  } = usePreviewData({
    pipelineId: pipelineIdFromUrl || '',
    node: previewNode,
    nodes: nodes as any[],
    edges: edges as any[],
    enabled: previewOpen && !!previewNode && !!pipelineIdFromUrl,
  });

  // Load pipeline from API
  useEffect(() => {
    if (pipelineData) {
      setPipelineName(pipelineData.name);
      setNodes(pipelineData.nodes);
      setEdges(pipelineData.edges);
    }
  }, [pipelineData, setEdges, setNodes]);

  // Load AI-generated pipeline from navigation state
  useEffect(() => {
    if (location.state?.generatedPipeline && location.state?.fromAI) {
      const { nodes: genNodes, edges: genEdges, explanation } = location.state.generatedPipeline;
      const safeNodes = Array.isArray(genNodes) ? genNodes.filter(isValidGeneratedNode) : [];
      const safeEdges = Array.isArray(genEdges) ? genEdges.filter(isValidGeneratedEdge) : [];

      if (safeNodes.length > 0) {
        setNodes(safeNodes as Node<PipelineNodeData>[]);
        setEdges(safeEdges as Edge[]);
        setPipelineName('AI Generated Pipeline');
        if (explanation) {
          toast.info(explanation);
        }

        if (
          (Array.isArray(genNodes) && safeNodes.length !== genNodes.length) ||
          (Array.isArray(genEdges) && safeEdges.length !== genEdges.length)
        ) {
          toast.warning('Sebagian node/edge dari AI tidak valid dan sudah diabaikan.');
        }
      } else if (Array.isArray(genNodes) && genNodes.length > 0) {
        toast.error('Pipeline AI tidak valid. Silakan generate ulang.');
      }
      // Clear location state to prevent re-loading on re-render
      window.history.replaceState({}, document.title);
    }
  }, []);

  // Keyboard shortcut for preview
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Shift+P to toggle preview
      if (e.shiftKey && e.key === 'P') {
        e.preventDefault();
        if (selectedNodeId) {
          setPreviewNodeId(selectedNodeId);
          setPreviewOpen((prev) => !prev);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedNodeId]);

  const handleUpdateNode = (id: string, config: Record<string, unknown>) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                ...config,
              } as PipelineNodeData,
            }
          : node
      )
    );
  };

  // Persist a single-node config change to the server immediately. Builds the
  // updated nodes array directly (instead of relying on async setNodes) and
  // then PUTs the full pipeline so the node config no longer requires a
  // separate global "Save Pipeline" click.
  const handleCommitNode = async (
    id: string,
    patch: Record<string, unknown>
  ): Promise<void> => {
    if (!pipelineIdFromUrl) {
      toast.error('Save the pipeline first before persisting individual nodes');
      return;
    }
    if (!pipelineName.trim()) {
      toast.error('Pipeline name is required');
      return;
    }

    const nextNodes = (nodes as PipelineNode[]).map((node) =>
      node.id === id
        ? ({
            ...node,
            data: { ...node.data, ...patch } as PipelineNodeData,
          } as PipelineNode)
        : node
    );

    try {
      const updated = await updatePipeline.mutateAsync({
        name: pipelineName.trim(),
        nodes: nextNodes,
        edges: edges as PipelineEdge[],
      });
      setNodes(updated.nodes as Node<PipelineNodeData>[]);
      setEdges(updated.edges as Edge[]);
    } catch {
      // useUpdatePipeline already shows a toast on error
    }
  };

  const handleAutoLayout = () => {
    const columnMap: Record<string, number> = {
      SOURCE: 0,
      TRANSFORMATION: 1,
      FILTER: 1,
      AGGREGATION: 1,
      JOIN: 1,
      UNION: 1,
      DESTINATION: 2,
      // Legacy support
      extract: 0,
      source: 0,
      transform: 1,
      transformation: 1,
      filter: 1,
      aggregation: 1,
      join: 1,
      union: 1,
      load: 2,
      destination: 2,
    };
    const counters: Record<string, number> = {
      SOURCE: 0,
      TRANSFORMATION: 0,
      FILTER: 0,
      AGGREGATION: 0,
      JOIN: 0,
      UNION: 0,
      DESTINATION: 0,
      // Legacy support
      extract: 0,
      source: 0,
      transform: 0,
      transformation: 0,
      filter: 0,
      aggregation: 0,
      join: 0,
      union: 0,
      load: 0,
      destination: 0,
    };

    setNodes((nds) =>
      nds.map((node) => {
        const column = columnMap[node.type as PipelineNodeType] ?? 0;
        const row = counters[node.type as PipelineNodeType] ?? 0;
        counters[node.type as PipelineNodeType] = row + 1;

        return {
          ...node,
          position: {
            x: 120 + column * 260,
            y: 80 + row * 140,
          },
        };
      })
    );
  };

  const handleSave = async (andClose = false) => {
    if (!pipelineName.trim()) {
      toast.error('Pipeline name is required');
      return;
    }

    const payload = {
      name: pipelineName.trim(),
      nodes: nodes as PipelineNode[],
      edges: edges as PipelineEdge[],
    };

    if (pipelineIdFromUrl) {
      const updated = await updatePipeline.mutateAsync(payload);
      setNodes(updated.nodes as Node<PipelineNodeData>[]);
      setEdges(updated.edges as Edge[]);
      if (andClose) {
        navigate('/pipelines');
      }
      return;
    }

    const created = await createPipeline.mutateAsync(payload);
    if (andClose) {
      navigate('/pipelines');
    } else {
      navigate(`/pipelines/${created.id}`, { replace: true });
    }
  };

  const handleSaveAndClose = () => handleSave(true);

  const handleBackToDashboard = () => {
    navigate('/pipelines');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBackToDashboard}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {pipelineIdFromUrl ? 'Edit Pipeline' : 'New Pipeline'}
            </h1>
            <p className="text-muted-foreground">
              Build and manage your ETL pipelines with drag-and-drop nodes.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <KeyboardShortcutsHelp />
          {pipelineIdFromUrl && (
            <Button
              variant="outline"
              onClick={() => setScheduleDialogOpen(true)}
              className="gap-2"
            >
              <CalendarClock className="h-4 w-4" />
              {pipelineData?.scheduleConfig ? 'Edit Schedule' : 'Schedule'}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleBackToDashboard}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={createPipeline.isPending || updatePipeline.isPending}
          >
            Save
          </Button>
          <Button
            onClick={handleSaveAndClose}
            disabled={createPipeline.isPending || updatePipeline.isPending}
          >
            {createPipeline.isPending || updatePipeline.isPending ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save & Close'
            )}
          </Button>
        </div>
      </div>

      {/* Pipeline Controls */}
      <div className="flex items-center gap-4 rounded-lg border bg-card p-4">
        <div className="flex-1">
          <label htmlFor="pipeline-name" className="text-sm font-medium">
            Pipeline Name
          </label>
          <input
            id="pipeline-name"
            type="text"
            value={pipelineName}
            onChange={(e) => setPipelineName(e.target.value)}
            className="w-full mt-1 px-3 py-2 rounded-md border bg-background"
            placeholder="Enter pipeline name..."
          />
        </div>
        <Button
          variant="outline"
          onClick={handleAutoLayout}
        >
          Auto Layout
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)_320px]">
        <NodePalette />
        <div className="min-w-0 space-y-4">
          <ReactFlowProvider>
            <PipelineCanvas
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              setNodes={setNodes}
              setEdges={setEdges}
              onSelectNode={(id) => setSelectedNodeId(id)}
              validationResult={validationResult}
            />
          </ReactFlowProvider>
          <PipelineBuilderChatPanel
            pipelineId={pipelineIdFromUrl}
            selectedNodeId={selectedNodeId}
            nodes={nodes as PipelineNode[]}
            edges={edges as PipelineEdge[]}
            validationResult={validationResult}
            onProposalApplied={async () => {
              if (!pipelineIdFromUrl) return;
              const refreshed = await refetchPipeline();
              if (refreshed.data) {
                setNodes(refreshed.data.nodes as Node<PipelineNodeData>[]);
                setEdges(refreshed.data.edges as Edge[]);
              }
            }}
          />
          <InsightPanel proposal={activeProposal} />
        </div>
        <div className="space-y-4">
          <NodeConfigPanel
            node={selectedNode}
            pipelineId={pipelineIdFromUrl || ''}
            edges={edges.map((e) => ({
              id: e.id,
              sourceId: e.source,
              targetId: e.target,
            }))}
            onUpdate={handleUpdateNode}
            onCommit={handleCommitNode}
	            onValidate={(node) => {
	              // Validate based on node type
	              if (node.data.type === 'SOURCE') {
	                const sourceConfig = node.data.config as Record<string, unknown> | undefined;
	                return typeof sourceConfig?.dataSourceId === 'string' && sourceConfig.dataSourceId.length > 0;
	              }
	              // Other node types are valid by default
	              return true;
	            }}
            onPreview={(nodeId) => {
              setPreviewNodeId(nodeId);
              setPreviewOpen(true);
            }}
          />
        </div>
      </div>

      {/* Validation and Health Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ValidationPanel
            validationResult={validationResult}
            onJumpToNode={(nodeId) => setSelectedNodeId(nodeId)}
          />
        </div>
        <div>
          <PipelineHealthIndicator
            healthScore={validationResult.healthScore}
            errorCount={validationResult.errors.length}
            warningCount={validationResult.warnings.length}
          />
        </div>
      </div>

      {/* Pipeline Execution Panel */}
      {pipelineIdFromUrl && (
        <PipelineExecutionPanel
          pipelineId={pipelineIdFromUrl}
          isActive={true}
        />
      )}

      {/* History and Publishing Tabs */}
      {pipelineIdFromUrl && (
        <Tabs defaultValue="history" className="space-y-4">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="history">Execution History</TabsTrigger>
              <TabsTrigger value="published">Published Datasets</TabsTrigger>
              {selectedRunId && <TabsTrigger value="logs">Execution Logs</TabsTrigger>}
            </TabsList>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPublishDialogOpen(true)}
            >
              <Share2 className="h-4 w-4 mr-2" />
              Publish Dataset
            </Button>
          </div>

          <TabsContent value="history" className="space-y-4">
            <ExecutionHistoryPanel
              pipelineId={pipelineIdFromUrl}
              onSelectRun={(runId) => {
                setSelectedRunId(runId);
                // Switch to logs tab when run is selected
                const logsTab = document.querySelector('[value="logs"]') as HTMLButtonElement;
                logsTab?.click();
              }}
            />
          </TabsContent>

          <TabsContent value="published" className="space-y-4">
            <ApiKeyManagement pipelineId={pipelineIdFromUrl} />
          </TabsContent>

          {selectedRunId && (
            <TabsContent value="logs" className="space-y-4">
              <ExecutionLogsViewer
                pipelineId={pipelineIdFromUrl}
                runId={selectedRunId}
                autoScroll={true}
              />
            </TabsContent>
          )}
        </Tabs>
      )}

      {/* Publish Dataset Dialog */}
      {pipelineIdFromUrl && (
        <PublishDatasetDialog
          pipelineId={pipelineIdFromUrl}
          open={publishDialogOpen}
          onOpenChange={setPublishDialogOpen}
        />
      )}

      {/* Schedule Editor Dialog */}
      {pipelineIdFromUrl && pipelineData && (
        <ScheduleEditorDialog
          open={scheduleDialogOpen}
          onOpenChange={setScheduleDialogOpen}
          pipelineId={pipelineIdFromUrl}
          pipelineName={pipelineData.name}
          currentSchedule={pipelineData.scheduleConfig}
          onSave={async (config: ScheduleConfig) => {
            await updateSchedule.mutateAsync(config);
            await refetchPipeline();
          }}
          onDelete={pipelineData.scheduleConfig ? async () => {
            await deleteSchedule.mutateAsync();
            await refetchPipeline();
          } : undefined}
        />
      )}

      {/* Floating Preview Panel */}
      {previewOpen && (
        <FloatingPreviewPanel
          nodeId={previewNodeId}
          preview={previewData || null}
          isLoading={previewLoading}
          error={previewError}
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
          onRefresh={() => refetchPreview()}
        />
      )}
    </div>
  );
}
